import React, { useContext } from "react";
import { AppointmentCredit, PlanForm } from "../PlanDrawer.types";
import ColorSquare from "../../../../components/common/ColorSquare/ColorSquare";
import { getCurrencySymbol } from "../../../../utils/currencyUtils";
import { Control, Controller } from "react-hook-form";
import styles from "./AppointmentCardV2.module.scss";
import { useAppointmentTypesQuery } from "../../../../apis/appointmentApi.hooks";
import { AppContext } from "../../../../context/app";
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@pushpress/pushpress-ui";

type Button = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

interface AppointmentCardPropsV2 extends Button {
  appointmentCredits: AppointmentCredit[];
  remove: (idx: number) => void;
  idx: number;
  control: Control<PlanForm>;
}

const AppointmentCardV2 = (props: AppointmentCardPropsV2) => {
  const { appointmentCredits, remove, idx, control } = props;

  const appointmentCredit = appointmentCredits[idx];

  const { client } = useContext(AppContext);
  const currencySymbol = getCurrencySymbol(client.currencyIso);

  const { data } = useAppointmentTypesQuery({
    staleTime: Infinity,
  });

  const appointmentTypes = data?.data.map((type) => ({
    uuid: type.uuid,
    name: type.name,
    color: type.color,
  }));

  return (
    <div className={"tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-wolf-200 tw-p-6 tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 tw-mb-3"}>
      <button
        type="button"
        onClick={() => remove(idx)}
        className="tw-absolute tw-top-2 tw-right-2 tw-text-wolf-400 hover:tw-text-red-500 tw-text-lg tw-font-bold tw-z-10"
        aria-label="Remove appointment credit"
      >
        ×
      </button>
      <ColorSquare
          color={appointmentCredit.appointmentType.color}
          className={styles.colorSquare}
          size="small"
        />  
        <div className="tw-gap-1.5" style={{ marginLeft: "24px" }}>
          <label htmlFor={`appointmentCredits.${idx}.appointmentType.uuid`}>
            Appointment type *
          </label>
        <Controller
          control={control}
          name={`appointmentCredits.${idx}.appointmentType.uuid`}
          render={({ field }) => (
              <Select 
                value={field.value}
                onValueChange={(value) => {
                  const appointmentType = appointmentTypes?.find(type => type.uuid === value);
                  if (!appointmentType) {
                    return;
                  }
                  appointmentCredits[idx].appointmentType = appointmentType;
                  field.onChange(appointmentType.uuid);
                }}
                required
                name={`appointmentCredits.${idx}.appointmentType`}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an appointment type" />
                </SelectTrigger>
                <SelectContent>
                  {appointmentTypes?.map((option) => (
                    <SelectItem key={option.uuid} value={option.uuid} disabled={appointmentCredits?.some(credit => credit?.appointmentType?.uuid === option.uuid)}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
          )}
        />
      </div>
      <div className="tw-gap-1.5">
        <label htmlFor={`appointmentCredits.${idx}.quantity`}>
          Quantity *
        </label>
        <Controller
          control={control}
          name={`appointmentCredits.${idx}.quantity`}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={(e) => field.onChange(parseInt(e.target.value))}
              type="number"
              min={1}
              placeholder="Quantity"
              autoComplete="off"
              required
              name={`appointmentCredits.${idx}.quantity`}
            />
          )}
      />
      </div>
      <div className="tw-gap-1.5">
        <label htmlFor={`appointmentCredits.${idx}.expirationDays`}>
          Expiration days *
        </label>
        <Controller
          control={control}
          name={`appointmentCredits.${idx}.expirationDays`}
          render={({ field }) => (
            <Input
              value={field.value}
              type="number"
              onChange={(e) => field.onChange(parseInt(e.target.value))}
              placeholder="Expiration days"
              min={1}
              autoComplete="off"
              required
              name={`appointmentCredits.${idx}.expirationDays`}
            />
          )}
        />
      </div>
      
      <div>
        <label htmlFor={`appointmentCredits.${idx}.value`}>
        Payroll value *
        </label>
        <div className="tw-flex tw-items-center tw-gap-0">
            <div className="tw-w-[28px] tw-h-9 tw-flex tw-items-center 
            tw-justify-center tw-rounded-l-md tw-bg-wolf-100 tw-border tw-border-wolf-200 tw-border-r-0">
                <span className="tw-text-sm tw-text-wolf-500">
                {currencySymbol}
                </span>
            </div>
            <Controller
                name={`appointmentCredits.${idx}.value`}
                control={control}
                render={({ field }) => (
                <Input
                    type="number"
                    min={0}
                    autoComplete="off"
                    {...field}
                    required
                    name={`appointmentCredits.${idx}.value`}
                    className="tw-rounded-l-none tw-flex-1"
                />
                )}
            />
            </div>
        </div>
    </div>
  );
};

export default AppointmentCardV2;
